import { get, identity, mapValues, pickBy } from 'lodash';
import type { ParametricSelector } from 'reselect';
import Url from 'url-parse';

import { TokenType } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';

export interface ResolvedTag {
  url: string;
  token?: string;
  signature?: string;
}

export interface ResolverState {
  [tag: string]: ResolvedTag;
}

export interface FetchTagPayload {
  tag: string;
  path?: string;
  open?: boolean;
  query?: object;
  tokenType?: number;
}

interface ResolverModel extends ModelConfig<ResolverState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

/* eslint-disable @typescript-eslint/naming-convention */
const tokenMap = {
  'address-change-support': TokenType.CustomerSupport,
  'bonuses-release-and-instant': TokenType.RationalWebServices,
  cashbonus: TokenType.RationalWebServices,
  'contact-support': TokenType.CustomerSupport,
  'free-spins-bonuses': TokenType.RationalWebServices,
  'freemium-buy-play-chips': TokenType.Freemium,
  'geo-assist': TokenType.CustomerSupport,
  'ice-home': TokenType.ICE,
  'ice-home2': TokenType.ICE,
  'ice-leaderboards': TokenType.RationalWebServices,
  'instant-bonuses': TokenType.RationalWebServices,
  'live-chat-support': TokenType.CustomerSupport,
  'mini-game-tickets': TokenType.RationalWebServices,
  'play-the-deal-webui': TokenType.WebAccountService,
  'verify-me-aml': TokenType.CustomerSupport,
  'web-real-money-form': TokenType.Cashier,
  bonuswheel_game: TokenType.WebAccountService,
  matchdaymillions_game: TokenType.WebAccountService,
  mc_vipstore: TokenType.VIPStore,
  open_bankid_validation: TokenType.Cashier,
  pickobject_game: TokenType.WebAccountService,
  pmc_paywall: TokenType.Freemium,
  rg_question: TokenType.CustomerSupport,
  singlereel_game: TokenType.WebAccountService,
  wc2fe_balance: TokenType.Cashier,
  wc2fe_cashout: TokenType.Cashier,
  wc2fe_convert_currencies: TokenType.Cashier,
  wc2fe_deposit: TokenType.Cashier,
  wc2fe_history: TokenType.Cashier,
  wc2fe_home: TokenType.Cashier,
  wc2fe_playmoney: TokenType.Cashier,
  wc2fe_preferred_currency: TokenType.Cashier
};
/* eslint-enable @typescript-eslint/naming-convention */

const reducers = {
  response: (state: ResolverState, payload: ResolverState) => ({ ...state, ...payload })
};

const effects = {
  async fetchTag(
    { tokenType, tag, query = {} }: FetchTagPayload,
    { dispatch, request }: EffectParams
  ) {
    try {
      const { body } = await request({
        service: 'tags.resolve',
        body: pickBy(
          {
            tag,
            tokenType: tokenType === undefined ? tokenMap[tag] : tokenType,
            urlParam: Url.qs.stringify(query)
          },
          identity
        )
      });

      const url = new Url(
        body.url.startsWith('external-') ? body.url.substring(9) : body.url,
        true
      );
      if (body.parameters) {
        body.parameters = mapValues(body.parameters, (parameter) => encodeURIComponent(parameter));
      }
      url.set('query', { ...query, ...body.parameters, ...url.query });
      dispatch.resolver.response({ [tag]: { url: url.toString(), ...body.parameters } });
    } catch (err) {
      // @TODO handle error
    }
  }
};

const selectors = (slice: Slicer<ResolverState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getTag() {
    return createSelector(
      slice,
      (rootState: RootState, tag: string) => tag,
      (state: ResolverState, tag: string) => state[tag]
    );
  },
  getUrl(this: { getTag: ParametricSelector<RootState, string, ResolvedTag | undefined> }) {
    return createSelector(this.getTag, (tag): string | undefined => get(tag, 'url'));
  }
});

const resolver: ResolverModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default resolver;
